import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { useApi } from '@/shared/api';
import { problemText } from '@/shared/api/problemText';
import { useApiProblem } from '@/shared/api/useApiProblem';
import { Button, Input, PageHeader } from '@/shared/components';
import {
  SurfaceLoadError,
  SurfaceLoading,
  SurfaceStaleHint,
} from '@/shared/components/SurfaceStates';
import { useOnline } from '@/shared/hooks/useOnline';
import { PhotoRequirementCard } from './slot-config/PhotoRequirementCard';
import { RoomListEditor } from './slot-config/RoomListEditor';
import { useSlotTemplate } from './slot-config/useSlotTemplate';

const CARD = 'border-hairline rounded-tile overflow-hidden border bg-white';

/**
 * Raumliste eines Apartments (Board B, „Apartment › Reinigung · Fotonachweis“): Schalter „Fotos
 * verlangen“, Räume in Reihenfolge, Bezeichnung. Gilt für Aufgaben, die nach dem Speichern entstehen.
 */
export function ChecklistTemplatePage() {
  const { propertyId } = useParams({ from: '/_admin/properties/$propertyId/checklist' });
  const api = useApi();
  const slots = useSlotTemplate(propertyId);
  const apartment = useQuery({
    queryKey: ['cleaning', 'apartment', propertyId],
    queryFn: () => api.properties.get(propertyId),
  });
  const problem = useApiProblem(slots.query.error);
  const name = apartment.data?.adminDisplayName ?? apartment.data?.name;
  const noRoom = slots.rooms.every((room) => !room.trim());
  const online = useOnline();
  const retry = (
    <SurfaceStaleHint
      onRetry={() => void slots.query.refetch()}
      retrying={slots.query.isFetching}
    />
  );

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-3">
      <Link
        to="/properties/$id/reinigung"
        params={{ id: propertyId }}
        className="text-forest flex min-h-11 items-center gap-1 self-start text-sm font-bold"
      >
        <ChevronLeft size={16} aria-hidden /> {name ?? 'Apartment'} · Reinigung
      </Link>
      <PageHeader
        className="mb-1"
        title="Raumliste"
        description="Daraus entsteht nach jeder Abreise die Reinigungsaufgabe."
      />
      {slots.query.data === undefined ? (
        <div className={CARD}>
          {slots.query.isError ? (
            <SurfaceLoadError
              message={problemText(problem, 'Die Raumliste konnte nicht geladen werden.')}
              onRetry={() => void slots.query.refetch()}
              retrying={slots.query.isFetching}
            />
          ) : online ? (
            <SurfaceLoading label="Raumliste wird geladen" rows={3} />
          ) : (
            <p role="status" className="text-ink-soft p-5 text-sm">
              Keine Verbindung – die Raumliste lädt, sobald du wieder Netz hast.
            </p>
          )}
        </div>
      ) : (
        <>
          {slots.query.isError && <div className={CARD}>{retry}</div>}
          <PhotoRequirementCard
            checked={slots.photos}
            mixed={slots.mixed}
            disabled={slots.save.isPending}
            onChange={slots.setPhotos}
          />
          <section aria-labelledby="rooms-title" className="flex flex-col gap-2">
            <b id="rooms-title" className="px-1 text-[15px]">
              Räume
            </b>
            <RoomListEditor rooms={slots.rooms} onChange={slots.setRooms} photos={slots.photos} />
            <p className="text-ink-soft px-1 text-xs leading-relaxed">
              Die Räume bleiben gespeichert – schaltest du die Fotos wieder ein, gilt dieselbe
              Liste. Leere Zeilen fallen beim Speichern weg.
            </p>
          </section>
          <label className="text-ink-soft flex flex-col gap-1 text-xs font-semibold">
            Bezeichnung
            <Input
              value={slots.name}
              maxLength={200}
              onChange={(e) => slots.setName(e.target.value)}
            />
          </label>
          <Button
            variant="brand"
            size="lg"
            className="self-start"
            loading={slots.save.isPending}
            disabled={(slots.photos && noRoom) || !slots.name.trim() || !online}
            onClick={() => slots.save.mutate()}
          >
            {slots.isCreate ? 'Raumliste anlegen' : 'Speichern'}
          </Button>
          {!online && (
            <p className="text-ink-soft text-xs">
              Keine Verbindung – Speichern geht wieder mit Netz.
            </p>
          )}
          {slots.photos && noRoom && (
            <p className="text-sealwax text-xs font-semibold">
              Mit Fotos braucht die Liste mindestens einen Raum.
            </p>
          )}
          {slots.isCreate && (
            <p className="text-ink-soft text-xs">
              Noch keine Raumliste: bis dahin entstehen Aufgaben ohne Fotos. Der Vorschlag ist
              vorbefüllt.
            </p>
          )}
        </>
      )}
    </div>
  );
}
